import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  AlertTriangle,
  Calendar,
  CheckCircle,
  Loader,
  Phone,
  Video,
  User
} from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import toast from 'react-hot-toast';

const UrgentBookingModal = ({ isOpen, onClose, studentInfo, assessmentResult }) => {
  const { post } = useApi();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [appointment, setAppointment] = useState(null);

  const [formData, setFormData] = useState({
    phone: '',
    sessionType: 'virtual', // Default to virtual for immediate access
    notes: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactRelation: ''
  });

  // Pre-fill student info from assessment
  useEffect(() => {
    if (studentInfo) {
      setFormData(prev => ({
        ...prev,
        fullName: studentInfo.name,
        email: studentInfo.email
      }));
    }
  }, [studentInfo]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create emergency appointment from high-risk assessment
      const response = await post('/emergency/create', {
        crisisType: 'high_risk_assessment',
        notes: `High-risk ${assessmentResult?.riskLevel || 'mental health'} assessment. ${formData.notes}`,
        sessionType: formData.sessionType,
        preferredTime: new Date().toISOString(),
        guestInfo: {
          fullName: studentInfo.name,
          email: studentInfo.email,
          phone: formData.phone,
          emergencyContact: formData.emergencyContactName && formData.emergencyContactPhone ? {
            name: formData.emergencyContactName,
            phone: formData.emergencyContactPhone,
            relationship: formData.emergencyContactRelation
          } : undefined
        },
        relatedAssessment: assessmentResult?.assessmentId
      });

      if (response?.data?.data) {
        setAppointment(response.data.data);
        setSuccess(true);
        toast.success('Urgent appointment created successfully!');
      }
    } catch (error) {
      console.error('Error creating urgent appointment:', error);
      toast.error(error.response?.data?.message || 'Failed to create appointment. Please call the crisis hotline.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={!success ? onClose : undefined}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              {/* Success State */}
              {success && appointment ? (
                <div className="p-8">
                  <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>

                  <div className="text-center">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-12 h-12 text-white" />
                    </div>
                    <h2 className="text-3xl font-display font-bold text-gray-900 mb-2">
                      Appointment Confirmed
                    </h2>
                    <p className="text-gray-600 mb-6">
                      A counselor has been notified and will contact you shortly
                    </p>

                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 mb-6 text-left">
                      <div className="space-y-4">
                        <div>
                          <p className="text-xs uppercase font-semibold text-gray-500 tracking-wide">Counselor</p>
                          <p className="font-semibold text-gray-900 mt-1">
                            {appointment.counselor?.fullName || 'Being Assigned'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs uppercase font-semibold text-gray-500 tracking-wide">Date & Time</p>
                          <p className="font-semibold text-gray-900 mt-1">
                            {new Date(appointment.date).toLocaleDateString('en-US', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })} at {appointment.timeSlot}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs uppercase font-semibold text-gray-500 tracking-wide">Session Type</p>
                          <p className="font-semibold text-gray-900 capitalize mt-1 flex items-center gap-2">
                            {appointment.sessionType === 'virtual' ? (
                              <><Video className="w-4 h-4" /> Virtual Session</>
                            ) : (
                              <><User className="w-4 h-4" /> In-Person Session</>
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs uppercase font-semibold text-gray-500 tracking-wide">Location</p>
                          <p className="font-semibold text-gray-900 mt-1">
                            {appointment.sessionType === 'physical'
                              ? 'Uzima Counseling Center'
                              : 'Virtual (Meeting link will be sent via email)'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {assessmentResult?.isCritical && (
                      <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-6">
                        <p className="text-sm font-semibold text-red-900 mb-2">
                          If you need immediate help before your appointment:
                        </p>
                        <div className="space-y-1">
                          <a
                            href="tel:0800720648"
                            className="flex items-center gap-2 text-sm text-red-700 hover:text-red-900 font-medium"
                          >
                            <Phone className="w-4 h-4" />
                            Befrienders Kenya: 0800 720 648 (24/7 Toll-Free)
                          </a>
                          <a
                            href="tel:999"
                            className="flex items-center gap-2 text-sm text-red-700 hover:text-red-900 font-medium"
                          >
                            <Phone className="w-4 h-4" />
                            Emergency Services: 999 or 112
                          </a>
                        </div>
                      </div>
                    )}

                    <p className="text-sm text-gray-600 mb-6">
                      A confirmation email has been sent to <strong>{studentInfo.email}</strong> with all appointment details.
                    </p>

                    <button
                      onClick={onClose}
                      className="w-full bg-gradient-uzima-primary text-white font-bold py-3 rounded-lg hover:shadow-lg transition-all"
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-display font-bold text-gray-900">
                        Book Urgent Counseling Session
                      </h2>
                      <p className="text-gray-600 mt-1">
                        Get immediate professional support
                      </p>
                    </div>
                    <button
                      onClick={onClose}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>

                  {/* Alert Banner */}
                  <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-bold text-red-900 mb-1">
                          {assessmentResult?.isCritical ? 'URGENT: ' : ''}High-Risk Assessment Result
                        </h3>
                        <p className="text-sm text-red-800">
                          Your assessment indicates you may benefit from immediate professional support. We're here to help.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Contact Information */}
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                      <h3 className="font-semibold text-gray-900 mb-4">Your Contact Information</h3>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Full Name
                          </label>
                          <input
                            type="text"
                            value={studentInfo.name}
                            disabled
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-100 text-gray-700"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Email Address
                          </label>
                          <input
                            type="email"
                            value={studentInfo.email}
                            disabled
                            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-100 text-gray-700"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Phone Number (Kenyan format) <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-uzima-maroon-500 focus:border-uzima-maroon-500 transition-all"
                            placeholder="0712345678 or +254712345678"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Required so the counselor can reach you
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Session Type */}
                    <div className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                      <label className="block text-sm font-semibold text-gray-700 mb-3">
                        Preferred Session Type <span className="text-red-500">*</span>
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <label
                          className={`flex items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                            formData.sessionType === 'virtual'
                              ? 'border-uzima-maroon-600 bg-gradient-to-br from-uzima-maroon-50 to-white shadow-md'
                              : 'border-gray-300 hover:border-uzima-maroon-300 hover:shadow'
                          }`}
                        >
                          <input
                            type="radio"
                            name="sessionType"
                            value="virtual"
                            checked={formData.sessionType === 'virtual'}
                            onChange={handleChange}
                            className="w-4 h-4 text-uzima-maroon-600 mr-3"
                          />
                          <Video className="w-5 h-5 mr-2" />
                          <span className="font-semibold">Virtual</span>
                        </label>
                        <label
                          className={`flex items-center justify-center p-4 border-2 rounded-xl cursor-pointer transition-all ${
                            formData.sessionType === 'physical'
                              ? 'border-uzima-maroon-600 bg-gradient-to-br from-uzima-maroon-50 to-white shadow-md'
                              : 'border-gray-300 hover:border-uzima-maroon-300 hover:shadow'
                          }`}
                        >
                          <input
                            type="radio"
                            name="sessionType"
                            value="physical"
                            checked={formData.sessionType === 'physical'}
                            onChange={handleChange}
                            className="w-4 h-4 text-uzima-maroon-600 mr-3"
                          />
                          <User className="w-5 h-5 mr-2" />
                          <span className="font-semibold">In-Person</span>
                        </label>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Virtual sessions are recommended for immediate access
                      </p>
                    </div>

                    {/* Additional Notes */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Additional Information <span className="text-gray-500 font-normal">(Optional)</span>
                      </label>
                      <textarea
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        rows="3"
                        placeholder="Any additional details you'd like to share with the counselor..."
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-uzima-maroon-500 focus:border-uzima-maroon-500 transition-all resize-none"
                      />
                    </div>

                    {/* Emergency Contact */}
                    <div className="border-t border-gray-200 pt-4">
                      <h4 className="font-semibold text-gray-900 mb-3">
                        Emergency Contact <span className="text-gray-500 font-normal">(Optional but recommended)</span>
                      </h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Contact Name
                          </label>
                          <input
                            type="text"
                            name="emergencyContactName"
                            value={formData.emergencyContactName}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-uzima-maroon-500 focus:border-uzima-maroon-500 transition-all"
                            placeholder="Contact person's name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Contact Phone
                          </label>
                          <input
                            type="tel"
                            name="emergencyContactPhone"
                            value={formData.emergencyContactPhone}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-uzima-maroon-500 focus:border-uzima-maroon-500 transition-all"
                            placeholder="0712345678"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Relationship
                          </label>
                          <input
                            type="text"
                            name="emergencyContactRelation"
                            value={formData.emergencyContactRelation}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-uzima-maroon-500 focus:border-uzima-maroon-500 transition-all"
                            placeholder="e.g., Parent, Sibling, Friend"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      disabled={loading || !formData.phone}
                      className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold py-4 rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg"
                    >
                      {loading ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          Creating Appointment...
                        </>
                      ) : (
                        <>
                          <Calendar className="w-5 h-5" />
                          Confirm Urgent Appointment
                        </>
                      )}
                    </button>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <p className="text-xs text-gray-600 text-center leading-relaxed">
                        By submitting this form, you consent to counseling services. Your information is kept strictly confidential.
                      </p>
                    </div>
                  </form>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default UrgentBookingModal;
